'use client';

import {
  AnimatePresence,
  MotionConfig,
  frame,
  motion,
  useMotionValue,
} from 'motion/react';
import { useState } from 'react';

import { useEscToClose } from '@core/hooks/useEscToClose';

import MotionButton from './Button';

const baseZIndex = 2000;
const zStack = ['overlay', 'thumbnail', 'image'];

export interface MotionCardToModalProps {
  layoutID: string;
  baseView: React.ReactNode;
  modalView: React.ReactNode;
}

const MotionCardToModal: React.FC<MotionCardToModalProps> = ({
  layoutID,
  baseView,
  modalView,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  useEscToClose(open, () => setOpen(false));

  return (
    <MotionConfig
      transition={{ type: 'spring', bounce: 0.1, visualDuration: 0.3 }}>
      <ItemBase id={layoutID} baseView={baseView} open={() => setOpen(true)} />

      <AnimatePresence>
        {open !== false && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key="overlay"
            className="overlay"
            onClick={() => setOpen(false)}
          />
        )}

        {open !== false && (
          <ModalView
            key="image"
            id={layoutID}
            modalView={modalView}
            onClick={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
      <Stylesheet />
    </MotionConfig>
  );
};

export default MotionCardToModal;

interface ItemBaseProps {
  id: string;
  baseView: React.ReactNode;
  open: () => void;
}
function ItemBase({ id, baseView, open }: ItemBaseProps) {
  const zIndex = useMotionValue(0);
  const activeZIndex = baseZIndex + zStack.indexOf('thumbnail');

  return (
    <motion.li
      className="item-base"
      key={id}
      onTap={() => {
        frame.postRender(() => {
          open();
          zIndex.set(activeZIndex);
        });
      }}
      layoutId={id}
      style={{ borderRadius: 10, zIndex }}
      onLayoutAnimationStart={() => zIndex.set(activeZIndex)}
      onLayoutAnimationComplete={() => zIndex.set(0)}>
      {baseView}
    </motion.li>
  );
}

interface ModalViewProps {
  id: string;
  modalView: React.ReactNode;
  onClick: () => void;
}
const ModalView: React.FC<ModalViewProps> = ({ id, modalView, onClick }) => {
  return (
    <div className="modal-view-wrapper">
      <motion.div layoutId={id} className="modal-view">
        {modalView}
        <MotionButton
          onClick={onClick}
          style={{ display: 'block', margin: '15px auto' }}>
          Dismiss
        </MotionButton>
      </motion.div>
    </div>
  );
};

function Stylesheet() {
  return (
    <style>
      {`
        .item-base {
          cursor: pointer;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: transform, opacity;
          touch-action: none;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
        }


        .item-base:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }

        .overlay {
          background: rgba(0, 0, 0, 0.6);
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          display: flex;
          pointer-events: none;
          z-index: ${baseZIndex + zStack.indexOf('overlay')};
          will-change: opacity;
        }

        .modal-view-wrapper {
          position: fixed;
          inset: 0;
          display: flex;
          justify-content: center;
          align-items: center;  
          z-index: ${baseZIndex + zStack.indexOf('image')};
        }

        .modal-view {
          // overflow: hidden;
          max-width: 90vw;
          max-height: 90vh;
          will-change: transform, opacity;
        }

        .modal-view img {
          width: 100%;
          height: 100%;
          will-change: transform;
        }
      `}
    </style>
  );
}
